import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Eraser, LoaderCircle, Split } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import AllocateDialog from '../components/AllocateDialog'
import ItemPicker from '../components/ItemPicker'
import MoneyLinesEditor from '../components/MoneyLinesEditor'
import { checkDraftLines, draftFromLines, mergeAllocation, sameLines, toLineInputs, type DraftLine } from '../lib/contracts'
import { formatMoney } from '../lib/money'
import type { CostTree } from '../lib/tree'
import type { CostContract, CostContractLineInput, CostContractLineView } from '../types'

interface Props {
  open: boolean
  contract: CostContract
  lines: CostContractLineView[]
  tree: CostTree
  projectId: string
  currency: string
  locale: string
  /** Awarded price handed over from "Z tendru": opens the pro-rata dialog with it. */
  initialAllocate: string | null
  onOpenChange: (open: boolean) => void
  /** Resolves true when the server accepted the lines; the dialog stays open on failure. */
  onSave: (lines: CostContractLineInput[]) => Promise<boolean>
}

const ContractLinesDialog: React.FC<Props> = ({ open, contract, lines, tree, projectId, currency, locale, initialAllocate, onOpenChange, onSave }) => {
  const { t } = useTranslation()
  const [draft, setDraft] = useState<DraftLine[]>([])
  const [allocating, setAllocating] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setDraft(draftFromLines(lines))
    setSaving(false)
    setAllocating(!!initialAllocate)
    // Only on opening: a background reload must not wipe the edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // ACTIVE: re-allocation only, at the server's current value (spec §1.4).
  const required = contract.status === 'ACTIVE' ? contract.totals.value : null
  const check = checkDraftLines(draft, required)
  const unchanged = sameLines(draft, lines)
  const money = (v: string) => formatMoney(v, { locale, currency })
  const showProblem = !!check.problem && (draft.length > 0 || required !== null)

  const save = async () => {
    if (check.problem || unchanged || saving) return
    setSaving(true)
    const ok = await onSave(toLineInputs(draft))
    setSaving(false)
    if (ok) onOpenChange(false)
  }

  return (
    <>
      <Dialog open={open} onOpenChange={(next) => { if (!saving) onOpenChange(next) }}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>{t('CostControl_lines_dialog_title')}</DialogTitle>
            <DialogDescription>{t(contract.status === 'ACTIVE' ? 'CostControl_lines_dialog_hint_active' : 'CostControl_lines_dialog_hint_draft')}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap items-center gap-2">
            <ItemPicker
              tree={tree}
              excludeIds={draft.map((l) => l.nodeId)}
              disabled={saving}
              onPick={(nodeId) => setDraft((d) => [...d, { nodeId, amount: null, valid: true, description: '' }])}
            />
            <Button variant="outline" size="sm" className="h-8" onClick={() => setAllocating(true)} disabled={saving}>
              <Split className="h-4 w-4" />
              {t('CostControl_lines_allocate')}
            </Button>
            {draft.length > 0 && (
              <Button variant="ghost" size="sm" className="h-8" onClick={() => setDraft([])} disabled={saving}>
                <Eraser className="h-4 w-4" />
                {t('CostControl_lines_clear')}
              </Button>
            )}
          </div>
          <MoneyLinesEditor lines={draft} tree={tree} disabled={saving} showProvisional onChange={setDraft} />
          <div className="flex flex-wrap items-center justify-end gap-x-6 gap-y-1 text-sm">
            <span>{t('CostControl_lines_total')}: <strong className="tabular-nums">{money(check.total)}</strong></span>
            {required !== null && <span className="text-muted-foreground">{t('CostControl_lines_required')}: <span className="tabular-nums">{money(required)}</span></span>}
            {check.difference !== null && check.difference !== '0.00' && (
              <span className="text-destructive">{t('CostControl_lines_difference')}: <span className="tabular-nums">{money(check.difference)}</span></span>
            )}
          </div>
          {showProblem && <p className="text-sm text-destructive">{t(check.problem as string)}</p>}
          <div className="flex items-center justify-end gap-2 border-t pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>{t('Common_cancel')}</Button>
            <Button onClick={save} disabled={saving || !!check.problem || unchanged}>
              {saving && <LoaderCircle className="h-4 w-4 animate-spin" />}
              {t('Common_save')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <AllocateDialog
        open={open && allocating}
        projectId={projectId}
        tree={tree}
        currency={currency}
        locale={locale}
        initialAmount={initialAllocate}
        onOpenChange={setAllocating}
        onApply={(alloc) => setDraft((d) => mergeAllocation(d, alloc))}
      />
    </>
  )
}
export default ContractLinesDialog
